import configService from "@/services/config";
import { API } from "./apiEndPoints";
import { postContextSuggestionStream } from "./suggestContextRoleApi";

export const CONTEXT_OBJECT_TYPES = ["responsibility", "role", "position", "department"];

const ENTITY_ENDPOINTS = {
  responsibility: API.SUGGEST_CONTEXT_RESPONSIBILITY,
  role: API.SUGGEST_CONTEXT_ROLE,
  position: API.SUGGEST_CONTEXT_POSITION,
  department: API.SUGGEST_CONTEXT_DEPARTMENT,
};

/**
 * Build the full POST URL for a saved-entity Context suggest operation
 * (ER-9893 responsibility/role/position, ER-9906 department).
 * @param {string} iqBaseUrl
 * @param {"responsibility"|"role"|"position"|"department"} objectType
 * @returns {string}
 */
export function buildSuggestContextEntityEndpoint(iqBaseUrl, objectType) {
  const path = ENTITY_ENDPOINTS[objectType];
  if (!path) {
    throw new Error(`Unsupported context object type: ${objectType}`);
  }
  return `${String(iqBaseUrl || "")
    .trim()
    .replace(/\/$/, "")}/${path}`;
}

/**
 * Build the POST body for a saved-entity Context suggestion.
 *
 * Seed turn: ids only. Refine turn: the artifact as it stands (`currentContext`)
 * plus the operator's instruction — `refine_prompt` for responsibility/role/position,
 * the chat `messages` for department (ER-9906 contract).
 *
 * @param {Object} params
 * @param {string} params.objectType
 * @param {number|string} params.accountId
 * @param {number|string} params.objectId
 * @param {string} [params.currentContext]
 * @param {string} [params.refinePrompt]
 * @param {Array<{role: string, content: string}>} [params.messages] - department chat turns
 * @returns {Object}
 */
export function buildSuggestContextEntityPayload({
  objectType,
  accountId,
  objectId,
  currentContext,
  refinePrompt,
  messages,
}) {
  const payload = {
    account_id: Number(accountId),
    [`${objectType}_id`]: Number(objectId),
  };
  const context = String(currentContext || "").trim();
  if (context) payload.current_context = context;

  if (objectType === "department") {
    if (Array.isArray(messages) && messages.length) payload.messages = messages;
  } else {
    const prompt = String(refinePrompt || "").trim();
    if (prompt) payload.refine_prompt = prompt;
  }
  return payload;
}

/**
 * Stream a Context suggestion (seed or refine) for a saved org object from IQ.
 * Resolves with the final context text; `onChunk` receives the accumulated text
 * while it streams, so an aborted request keeps what was already written.
 *
 * @param {Object} params
 * @param {string} params.objectType
 * @param {number|string} params.accountId
 * @param {number|string} params.objectId
 * @param {string} [params.currentContext]
 * @param {string} [params.refinePrompt]
 * @param {Array<{role: string, content: string}>} [params.messages]
 * @param {AbortSignal} [params.signal]
 * @param {Function} [params.onChunk]
 * @param {Function} [params.onComplete]
 * @param {Function} [params.onError]
 * @param {Function} [params.onThinking]
 * @param {Function} [params.onPhase]
 * @param {Function} [params.onStalled]
 * @returns {Promise<string>}
 */
export async function suggestContextEntityStream({
  objectType,
  accountId,
  objectId,
  currentContext,
  refinePrompt,
  messages,
  ...handlers
}) {
  const iqBaseUrl = configService.get("iqBaseUrl");
  return postContextSuggestionStream({
    url: buildSuggestContextEntityEndpoint(iqBaseUrl, objectType),
    payload: buildSuggestContextEntityPayload({
      objectType,
      accountId,
      objectId,
      currentContext,
      refinePrompt,
      messages,
    }),
    headers: { Accept: "text/event-stream" },
    ...handlers,
  });
}
